import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

const RELOAD_KEY = 'peak:chunk-reload';
const RELOAD_COOLDOWN_MS = 10_000;

/** True when a dynamic import failed because the hashed chunk is gone (typical after deploy). */
export function isChunkLoadError(error: unknown): boolean {
  const message = String(
    error instanceof Error ? error.message : error ?? '',
  ).toLowerCase();
  return (
    message.includes('failed to fetch dynamically imported module') ||
    message.includes('error loading dynamically imported module') ||
    message.includes('importing a module script failed') ||
    message.includes('loading chunk') ||
    message.includes('loading css chunk')
  );
}

/**
 * After a frontend deploy, hashed `/assets/*.js` chunks are replaced. Tabs that
 * still hold the old shell try to lazy-load a deleted file; CloudFront's SPA
 * fallback returns index.html, and the browser throws a module-load error.
 * One hard reload picks up the new index.html and matching chunk hashes.
 */
export function reloadOnceForChunkError(): boolean {
  try {
    const last = sessionStorage.getItem(RELOAD_KEY);
    const now = Date.now();
    if (last && now - Number(last) < RELOAD_COOLDOWN_MS) {
      return false;
    }
    sessionStorage.setItem(RELOAD_KEY, String(now));
  } catch {
    // sessionStorage unavailable — still attempt a single reload
  }
  window.location.reload();
  return true;
}

/**
 * Await a dynamic import; on stale-chunk failure, hard-reload once and hang
 * the promise so callers don't surface a transient error during reload.
 */
export async function importWithChunkRetry<T>(factory: () => Promise<T>): Promise<T> {
  try {
    return await factory();
  } catch (error) {
    if (isChunkLoadError(error) && reloadOnceForChunkError()) {
      return new Promise(() => {});
    }
    throw error;
  }
}

/**
 * Fire-and-forget prefetch that recovers from stale chunks the same way.
 * Non-chunk failures are swallowed (prefetch must never break navigation).
 */
export function prefetchWithChunkRetry(factory: () => Promise<unknown>): void {
  void importWithChunkRetry(factory).catch(() => {
    /* ignore non-chunk prefetch failures */
  });
}

/**
 * Drop-in for React.lazy that recovers from stale chunk URLs after deploy.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- ComponentType<unknown>
// rejects components with typed props (props are contravariant); `any` matches React.lazy.
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return lazy(() => importWithChunkRetry(factory));
}
